/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { MeetingService } from '../../apps/meeting/services/MeetingService';
import { isSelfUserPid } from './workplace.store';
import { jitsiService } from '../../shared/singletons/jitsi-singleton';
import { useMeetingStore } from './meeting.store';

export type BreakoutRoom = {
  id: string;
  name?: string;
  jid: string;
  isMainRoom?: boolean;
  participants: Record<string, any>;
};

type BreakoutStore = {
  breakoutCreatorUserPid?: string;
  breakoutRooms: Record<string, BreakoutRoom>;
  mainMeetingRoom?: BreakoutRoom;
  activeRoomId: string | null;
  isSelfUserModerator: boolean;

  setActiveRoomId: (id: string) => void;
  setBreakoutRooms: (room: Record<string, BreakoutRoom>) => void;
  removeBreakoutRoom: (id: string) => void;
  updateParticipants: (roomId: string, participants: string[]) => void;
  setMainMeetingRoom: (room: any) => void;
  setBreakoutRoomCreatorPid: (userPid?: string) => void;
  getRoomsLength: () => number;
};

export const useBreakoutStore = create<BreakoutStore>((set, get) => ({
  breakoutRooms: {},
  mainMeetingRoom: undefined,
  activeRoomId: null,
  breakoutCreatorUserPid: undefined,
  isSelfUserModerator: true,
  setMainMeetingRoom: (data) => set({ mainMeetingRoom: data }),
  setActiveRoomId: (id) => set({ activeRoomId: id }),
  setBreakoutRooms: (breakoutRooms) => {
    let mainMeetingRoom: any = null;
    for (const key in breakoutRooms) {
      if (breakoutRooms[key].isMainRoom) {
        mainMeetingRoom = breakoutRooms[key];
        delete breakoutRooms[key];
        break;
      }
    }

    set({ mainMeetingRoom, breakoutRooms });
  },
  removeBreakoutRoom: (id) => {
    set((state) => {
      const { [id]: unused, ...rest } = state.breakoutRooms;
      return { breakoutRooms: rest };
    });
  },
  setBreakoutRoomCreatorPid: (userPid?: string) => {
    const isSelfUserModerator = userPid ? isSelfUserPid(userPid) : true;
    set(() => ({ breakoutCreatorUserPid: userPid, isSelfUserModerator }));
  },
  updateParticipants: (roomId, participants) => {
    set((state) => ({
      breakoutRooms: {
        ...state.breakoutRooms,
        [roomId]: {
          ...state.breakoutRooms[roomId],
          participants,
        },
      },
    }));
  },
  getRoomsLength: () => Object.keys(get().breakoutRooms ?? {}).length,
}));

export const autoAssignParticipants = async ({
  expectedRoomCount,
  includeCreator = true,
}: {
  expectedRoomCount: number;
  includeCreator?: boolean;
}) => {
  await ensureRoomsCreated({ expectedCount: expectedRoomCount });

  const { breakoutCreatorUserPid: creatorPid, breakoutRooms } = useBreakoutStore.getState();

  const rooms = Object.values(breakoutRooms);
  const participants = Object.values(useMeetingStore.getState().jitsiParticipants);

  const eligible = participants.filter(
    (p) => p?.userPid && (includeCreator || p.userPid !== creatorPid)
  );
  if (!rooms.length || eligible.length < 2) return;

  const queue = shuffle(eligible);
  const assignments: Record<string, any[]> = {};
  for (const room of rooms) assignments[room.id] = [];

  for (const room of rooms) {
    if (queue.length >= 2) assignments[room.id].push(queue.shift()!, queue.shift()!);
  }

  let idx = 0;
  while (queue.length) {
    const room = rooms[idx % rooms.length];
    assignments[room.id].push(queue.shift()!);
    idx++;
  }

  for (const room of rooms) {
    for (const person of assignments[room.id]) {
      await jitsiService?.sendParticipantToBreakoutRoom?.(person.pid, room.id);
    }
  }

  return assignments;
};

const ensureRoomsCreated = ({
  expectedCount,
  intervalMs = 800,
  maxAttempts = 10,
}: {
  expectedCount: number;
  intervalMs?: number;
  maxAttempts?: number;
}): Promise<void> => {
  return new Promise((resolve) => {
    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts++;
      const currentCount = useBreakoutStore.getState().getRoomsLength();
      if (currentCount >= expectedCount || attempts >= maxAttempts) {
        clearInterval(intervalId);
        resolve();
      }
    }, intervalMs);
  });
};

function shuffle<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
